// FILE: src/components/ReadinessExtras.js
//
// Below-the-fold sections of the redesigned Readiness page:
//   FactorGuide       — below-the-fold explanations of the six factors
//   HistoryCard       — readiness-score mini-chart from readiness_snapshots
//                       (journey milestones reuse the dashboard's JourneyRoadmap)
//   ContributionTable — compact "how the score is calculated" table
//   ReminderBanner    — branded PM Pixel consistency nudge
//
// Pure presentation; data comes in via props from ReadinessPage.

import React from 'react';
import styles from './ReadinessExtras.module.css';
import CoachAvatar from '../coach/CoachAvatar';
import {
  METRIC_META, METRIC_ORDER, buildSparkline, contributionRows,
} from './readinessPageHelpers';

export function FactorGuide({ readiness }) {
  const inputs = readiness?.inputs || {};
  return (
    <section className={styles.guideWrap} data-testid="readiness-factor-guide">
      <div className={styles.eyebrow}>The six factors, explained</div>
      <div className={styles.guideGrid}>
        {METRIC_ORDER.map((key) => {
          const meta = METRIC_META[key];
          const item = inputs[key];
          return (
            <article key={key} className={styles.guideCard}>
              <div className={styles.guideTitle}>
                <span aria-hidden="true">{meta.icon}</span> {meta.title}
                <span className={styles.guideWeight}>{Math.round((item?.weight || 0) * 100)}%</span>
              </div>
              <p className={styles.guideText}>{meta.summary} {meta.detail}</p>
              {item?.label && <p className={styles.guideCoach}>{item.label}</p>}
            </article>
          );
        })}
      </div>
    </section>
  );
}

export function HistoryCard({ snapshots, trend, targetPct = 75 }) {
  const geo = buildSparkline(snapshots);
  const trendValid = !!(trend && trend.sufficient && typeof trend.delta === 'number');
  return (
    <section className={`${styles.card} ${styles.histCard}`} data-testid="readiness-history">
      <header className={styles.cardHead}>
        <span className={styles.eyebrow}>Readiness History</span>
        {trendValid && (
          <span className={`${styles.deltaChip} ${trend.direction === 'up' ? styles.deltaUp : trend.direction === 'down' ? styles.deltaDown : ''}`}>
            {trend.delta > 0 ? '+' : ''}{trend.delta} pts this week
          </span>
        )}
      </header>
      {geo ? (
        <svg
          viewBox={`0 0 ${geo.width} ${geo.height}`}
          className={styles.chart}
          role="img"
          aria-label={`Readiness score over time, most recently ${Math.round(geo.last.score)} out of 100`}
        >
          <line
            x1="0" x2={geo.width}
            y1={geo.yFor(targetPct)} y2={geo.yFor(targetPct)}
            className={styles.targetLine}
          />
          <polyline points={geo.line} className={styles.trendLine} />
          {geo.pts.map((p, i) => (
            <circle key={i} cx={p.x} cy={p.y} r={i === geo.pts.length - 1 ? 4.5 : 3}
              className={i === geo.pts.length - 1 ? styles.dotLast : styles.dot} />
          ))}
        </svg>
      ) : (
        <p className={styles.emptyNote}>
          Your score history shows up here after a couple of sessions — check
          back once you’ve practiced again.
        </p>
      )}
      {geo && (
        <p className={styles.chartCaption}>
          Dashed line marks the {targetPct}-point exam-ready target.
        </p>
      )}
    </section>
  );
}

export function ContributionTable({ readiness }) {
  const { rows, total } = contributionRows(readiness);
  if (rows.length === 0) return null;
  return (
    <section className={`${styles.card} ${styles.calcCard}`} data-testid="readiness-contribution">
      <header className={styles.cardHead}>
        <span className={styles.eyebrow}>How your score is calculated</span>
      </header>
      <div className={styles.tableScroll}>
      <table className={styles.calcTable}>
        <thead>
          <tr><th>Input</th><th>Weight</th><th>Score</th><th>Contributes</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td>{r.title}</td>
              <td>{r.weightPct}%</td>
              <td>{r.value}/100</td>
              <td>{r.points} pts</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan="3">Readiness score</td>
            <td>{typeof readiness?.score === 'number' ? readiness.score : total} pts</td>
          </tr>
        </tfoot>
      </table>
      </div>
      <p className={styles.calcNote}>
        Each input is scored 0–100, multiplied by its weight, and summed.
        Contributions are rounded, so they can differ from the total by a point.
      </p>
    </section>
  );
}

export function ReminderBanner({ onStartPractice }) {
  return (
    <section className={styles.banner} data-testid="readiness-banner">
      <CoachAvatar pose="cheer" heightPx={96} decorative />
      <div className={styles.bannerText}>
        <div className={styles.bannerTitle}>Consistency beats cramming.</div>
        <p className={styles.bannerMsg}>
          A short, focused session today does more for your readiness than a
          marathon next week. PM Pixel will be here either way.
        </p>
      </div>
      <button type="button" className={styles.bannerBtn}
        onClick={onStartPractice} data-testid="readiness-banner-cta">
        Start a Session
      </button>
    </section>
  );
}
